import { type FC, useEffect, useMemo, createContext, useContext, type ReactNode } from 'react';
import { useQueryLoader, type PreloadedQuery, graphql } from 'react-relay';
import { getBuyerId } from 'dibs-cookie-jar';

import {
    MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS,
    CAROUSEL_RECENTLY_VIEWED_ITEMS_PER_PAGE,
    CAROUSEL_RECENTLY_VIEWED_ITEMS_PER_PAGE_MOBILE,
    MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS_TABLET,
    CAROUSEL_RECENTLY_VIEWED_ITEMS_PER_PAGE_TABLET,
} from '../recentHistoryConstants';
import { getLocalRecentHistoryList } from '../getLocalRecentHistory';
import { type LocalHistoryItemType } from '../recentHistoryTypes';

import {
    type RecentlyViewedItemsContextQuery,
    type RecentlyViewedItemsContextQuery$variables,
} from './__generated__/RecentlyViewedItemsContextQuery.graphql';

export const MOBILE = 'mobile' as const;
export const TABLET = 'tablet' as const;
export const DESKTOP = 'desktop' as const;

export type DeviceType = typeof MOBILE | typeof TABLET | typeof DESKTOP;

type Context = {
    queryReference?: PreloadedQuery<RecentlyViewedItemsContextQuery> | null;
    localStorageItems: LocalHistoryItemType[];
    userId: string;
    itemsPerPage: number;
    deviceType: DeviceType;
};
const RecentlyViewedItemsContext = createContext<Context | null>(null);
export const useRecentlyViewedItemsContext = (): Context => {
    const context = useContext(RecentlyViewedItemsContext);
    if (!context) {
        throw new Error('RecentlyViewedItemsProvider was not found');
    }

    return context;
};

export const recentlyViewedItemsQuery = graphql`
    query RecentlyViewedItemsContextQuery(
        $userId: String!
        $excludeItemPks: [String] = []
        $count: Int = 0
        $fetchUserData: Boolean = false
        $selectedItemIds: [String] = []
        $loadPortfolioData: Boolean = false
        $userIds: [String] = []
        $itemIds: [String] = []
    ) {
        viewer {
            user(userId: $userId) @include(if: $fetchUserData) {
                ...RecentlyViewedItemsCarousel_user
                    @arguments(excludeItemPks: $excludeItemPks, count: $count)
            }
            ...RecentlyViewedItemsCarousel_viewer
                @arguments(
                    loadPortfolioData: $loadPortfolioData
                    selectedItemIds: $selectedItemIds
                    userIds: $userIds
                )
            itemRead(itemIds: $itemIds) {
                ...RecentlyViewedItemsCarousel_items
            }
        }
    }
`;

const getQueryArgs = ({
    localStorageItems,
    maxItems,
    excludedItemIds,
    userId,
}: {
    localStorageItems: LocalHistoryItemType[];
    maxItems: number;
    excludedItemIds: string[] | null;
    userId: string;
}): RecentlyViewedItemsContextQuery$variables => {
    const hasUserId = !!userId;

    const itemIds = localStorageItems.map(item => item.itemId);
    const remainingItemsCount = maxItems - itemIds.length;
    const fetchRemainingItems = remainingItemsCount > 0;

    return {
        userId,
        excludeItemPks: [...itemIds, ...(excludedItemIds || [])],
        count: remainingItemsCount,
        //fetch user data only if maxItems is not reached and we have user id
        fetchUserData: hasUserId && fetchRemainingItems,

        //fetch favorites only if we have items in the local storage and we have user id
        loadPortfolioData: hasUserId && !!itemIds.length,
        selectedItemIds: itemIds,
        userIds: hasUserId ? [userId] : [],
        itemIds,
    };
};

export const RecentlyViewedItemsProvider: FC<{
    excludedItemIds?: string[] | null;
    children: ReactNode;
    deviceType?: DeviceType;
}> = ({ excludedItemIds = null, children, deviceType = DESKTOP }) => {
    const isMobile = deviceType === MOBILE;
    const isTablet = deviceType === TABLET;

    const maxItems = isTablet
        ? MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS_TABLET
        : MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS;
    let itemsPerPage = CAROUSEL_RECENTLY_VIEWED_ITEMS_PER_PAGE;
    if (isMobile) {
        itemsPerPage = CAROUSEL_RECENTLY_VIEWED_ITEMS_PER_PAGE_MOBILE;
    } else if (isTablet) {
        itemsPerPage = CAROUSEL_RECENTLY_VIEWED_ITEMS_PER_PAGE_TABLET;
    }

    const [queryReference, loadQuery] =
        useQueryLoader<RecentlyViewedItemsContextQuery>(recentlyViewedItemsQuery);

    const localStorageItems = useMemo(
        //excludedItemId excludes provided item from local storage
        () => getLocalRecentHistoryList(excludedItemIds, maxItems).reverse(),
        [excludedItemIds, maxItems]
    );

    const userId = typeof document !== 'undefined' ? getBuyerId(document.cookie) : '';
    const queryArgs = useMemo(
        () => getQueryArgs({ localStorageItems, maxItems, excludedItemIds, userId }),
        [localStorageItems, maxItems, excludedItemIds, userId]
    );

    useEffect(() => {
        loadQuery(queryArgs);
    }, [loadQuery, queryArgs]);

    const contextValue = useMemo(() => {
        return {
            queryReference,
            localStorageItems,
            userId: queryArgs.userId,
            itemsPerPage,
            deviceType,
        };
    }, [queryReference, localStorageItems, queryArgs.userId, itemsPerPage, deviceType]);

    return (
        <RecentlyViewedItemsContext.Provider value={contextValue}>
            {children}
        </RecentlyViewedItemsContext.Provider>
    );
};
